/* eslint-disable react-hooks/rules-of-hooks */
"use client";

import * as React from "react";
import Link from "next-shims/Link";
import {
  Box,
  Chip,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
import GroupIcon from "@mui/icons-material/Group";
import { useI18n } from "i18n/I18nProvider";

function fmtPrice(p: any) {
  if (!p) return null;
  const n = Number(p);
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat("el-GR").format(n);
}

export default function FeaturedPropertiesCarousel({
  locale,
  items,
  intervalMs = 4500,
}: {
  locale: string;
  items: any[];
  intervalMs?: number;
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const ref = React.useRef<HTMLDivElement | null>(null);
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
const [inView, setInView] = React.useState(true);
const { t } = useI18n();

  const safeItems = items?.filter(Boolean) || [];
  const count = safeItems.length;

  // scroll to slide
const goTo = React.useCallback((i: number) => {
  const root = ref.current;
  if (!root || !count) return;

  const next = ((i % count) + count) % count;
  const el = root.children.item(next) as HTMLElement | null;
  if (!el) return;

  // ✅ οριζόντιο scroll ΜΟΝΟ μέσα στο carousel (δεν πειράζει το page scroll)
  root.scrollTo({ left: el.offsetLeft, behavior: "smooth" });

  setIndex(next);
}, [count]);

React.useEffect(() => {
  const root = ref.current;
  if (!root) return;

  const io = new IntersectionObserver(
    (entries) => setInView(entries[0]?.isIntersecting ?? true),
    { threshold: 0.35 }
  );

  io.observe(root);
  return () => io.disconnect();
}, []);


  // autoplay
React.useEffect(() => {
  if (!count) return;
  if (paused) return;
  if (!inView) return;

  const t = window.setInterval(() => {
    goTo(index + 1);
  }, intervalMs);

  return () => window.clearInterval(t);
}, [count, paused, inView, goTo, index, intervalMs]);


  // track index on manual scroll (mobile swipe)
  React.useEffect(() => {
    const root = ref.current;
    if (!root || !count) return;

    const onScroll = () => {
      const children = Array.from(root.children) as HTMLElement[];
      const left = root.scrollLeft;
      let best = 0;
      let bestDist = Infinity;

      children.forEach((c, i) => {
        const dist = Math.abs(c.offsetLeft - left);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });

      setIndex(best);
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, [count]);

  if (!count) return null;

  const next = React.useCallback(() => {
  goTo(index + 1);
}, [goTo, index]);

const prev = React.useCallback(() => {
  goTo(index - 1);
}, [goTo, index]);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <Box
        ref={ref}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          gap: 16,
          pb: 0.5,
        }}
      >
        {safeItems.map((p, i) => {
          const title = p.display_title || "";
          const city = p.city || p.neighborhood || "";
          const region = p.region || "";
          const type = p.property_type || "";
          const status = p.listing_status || "";
          const label = p.label || "";
          const bedrooms = p.bedrooms ?? null;
          const bathrooms = p.bathrooms ?? null;
          const persons = p.persons ?? null;
          const cover = p.coverImage || p.featured_image_url || "/placeholder.jpg";

          const priceOnCall = !!p.price_on_call;
          const price = priceOnCall ? null : fmtPrice(p.price);
          const currency = p.price_currency || "EUR";

          // 1 slide at a time: width 100%
          return (
            <Box
              key={p.id ?? i}
              sx={{
                flex: "0 0 100%",
                scrollSnapAlign: "start",
                position: "relative",
                borderRadius: 4,
                overflow: "hidden",
                height: { xs: 360, sm: 420, md: 460 }, // ✅ σταθερό ύψος = premium consistency
              }}
            >
              <Link
                href={`/${locale}/properties/${p.slug}`}
                style={{ display: "block", width: "100%", height: "100%" }}
              >
                {/* Background image */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${cover})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    transform: "scale(1.02)",
                  }}
                />

                {/* Gradient overlays */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.65) 100%)",
                  }}
                />

                {/* Top-left chips */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ position: "absolute", top: 16, left: 16, right: 16 }}
                >
                  {label ? (
                    <Chip
                      size="small"
                      label={label}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.92)",
                        fontWeight: 800,
                      }}
                    />
                  ) : null}
                  {status ? (
                    <Chip
                      size="small"
                      label={status.toUpperCase()}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.20)",
                        color: "white",
                        border: "1px solid rgba(255,255,255,0.35)",
                        fontWeight: 800,
                      }}
                    />
                  ) : null}
                  {type ? (
                    <Chip
                      size="small"
                      label={type}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.16)",
                        color: "white",
                        border: "1px solid rgba(255,255,255,0.25)",
                      }}
                    />
                  ) : null}
                </Stack>

                {/* Bottom info overlay */}
                <Box sx={{ position: "absolute", left: 16, right: 16, bottom: 16 }}>
                  <Stack spacing={1}>
                    <Typography
                      variant={isMdUp ? "h5" : "h6"}
                      sx={{
                        color: "white",
                        fontWeight: 900,
                        letterSpacing: -0.4,
                        textShadow: "0 6px 18px rgba(0,0,0,0.45)",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.88)",
                        fontWeight: 600,
                      }}
                    >
                      {[city, region].filter(Boolean).join(", ")}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {bedrooms != null ? (
                        <Chip
                          size="small"
                          icon={<BedIcon />}
label={`${bedrooms} ${t("property.beds_abbr")}`}
                          sx={chipDark}
                        />
                      ) : null}
                      {bathrooms != null ? (
                        <Chip
                          size="small"
                          icon={<BathtubIcon />}
label={`${bathrooms} ${t("property.baths_abbr")}`}
                          sx={chipDark}
                        />
                      ) : null}
                      {persons != null ? (
                        <Chip
                          size="small"
                          icon={<GroupIcon />}
label={`${persons} ${t("property.persons_abbr")}`}
                          sx={chipDark}
                        />
                      ) : null}

                      <Box sx={{ flexGrow: 1 }} />

                      <Chip
                        size="small"
label={priceOnCall ? t("price_on_call") : price ? `${price} ${currency}` : t("common.na")}
                        sx={{
                          ...chipDark,
                          fontWeight: 900,
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Link>
            </Box>
          );
        })}
      </Box>

      {/* Arrows (desktop) */}
      {count > 1 ? (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.16)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
              display: { xs: "none", md: "inline-flex" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.16)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
              display: { xs: "none", md: "inline-flex" },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Dots */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 10,
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            {Array.from({ length: count }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: i === index ? 22 : 8,
                  height: 8,
                  borderRadius: 99,
                  bgcolor: i === index ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)",
                  transition: "all .2s ease",
                }}
              />
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

const chipDark = {
  bgcolor: "rgba(0,0,0,0.28)",
  color: "white",
  border: "1px solid rgba(255,255,255,0.18)",
  backdropFilter: "blur(8px)",
};

function hintUserScroll(root: HTMLDivElement | null) {
  // no-op helper: κρατά το interaction natural (και για μελλοντικά tweaks)
  if (!root) return;
}
